import type {
  DetectedHdpeContent,
  DetectedPetContent,
  DetectedUbcContent,
  EvidenceUploadMode,
  SimpleMaterialQualityCategory,
} from 'api';
import { useTranslation } from 'i18n';
import { Box, Stack } from 'ui';

import { TargetBaleWeightsHistogram } from '../../../charts';
import { PhotoEvidenceGalleryWithDialog } from '../../../evidence';
import { useGetRepresentativeEvidence } from '../../hooks/useGetRepresentativeEvidence/useGetRepresentativeEvidence';
import { RepresentativeEvidenceFilter } from '../RepresentativeEvidenceFilter/RepresentativeEvidenceFilter';

type Props = {
  detectedContent: DetectedUbcContent | DetectedPetContent | DetectedHdpeContent;
  parentId?: number;
  uploadMode?: EvidenceUploadMode;
  targetName?: string;
  materialCategory?: Pick<SimpleMaterialQualityCategory, 'id' | 'name'> | null;
};

export const RepresentativeEvidenceSection = ({
  detectedContent,
  parentId,
  uploadMode,
  targetName = 'Target',
  materialCategory,
}: Props) => {
  const { t } = useTranslation('features');
  const { representativeType, setRepresentativeType, representativeEvidence, isRepresentativeEvidenceLoading } =
    useGetRepresentativeEvidence({
      detectedContent,
      parentId,
      uploadMode,
    });
  const hasEvidenceToShow = isRepresentativeEvidenceLoading || Boolean(representativeEvidence?.length);

  return (
    <>
      <TargetBaleWeightsHistogram
        targetBaleWeights={detectedContent.target_bale_weights}
        targetGrade={detectedContent.average_target_content * 100}
        title={t('backlog.breakdowns.representativeEvidenceSection.distributionTitle', {
          values: { targetName },
        })}
      />
      <Box
        sx={{
          display: 'flex',
          mt: 2,
        }}
      >
        <RepresentativeEvidenceFilter value={representativeType} onChange={setRepresentativeType} />
      </Box>
      {hasEvidenceToShow ? (
        <Stack
          direction="column"
          sx={{
            gap: 1,
            mt: 1,
          }}
        >
          <PhotoEvidenceGalleryWithDialog
            evidence={representativeEvidence ?? []}
            isLoading={isRepresentativeEvidenceLoading}
            materialCategory={materialCategory}
          />
        </Stack>
      ) : null}
    </>
  );
};
